<template>
    <b-row>
        <b-col :md="12">
            <div class="pb-1 d-flex">
                <h4>
                    {{ $t('org.dashboard.Dashboard Preview') }}
                </h4>
                <span>
                    <b-badge
                        variant="primary"
                        class="ml-25 font-small-1"
                    >
                        {{ $t('v4.Sample') }}
                    </b-badge>
                </span>
            </div>
            <div>
                <b-alert
                    v-height-fade
                    show
                    dismissible
                    fade
                    variant="warning"
                >
                    <div class="alert-body">
                        <span>
                            {{ $t('org.dashboard.Sample data is used in edit mode') }}
                        </span>
                    </div>
                </b-alert>
            </div>
            <div class="pointer-events-none">
                <b-row class="mb-1">
                    <b-col :md="4" class="pr-md-0">
                        <b-form-group>
                            <v-select
                                class="common-select common-select-editmode"
                                :placeholder="$t('v4.Group')"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col :md="6" class="pr-md-0">
                        <b-form-group>
                            <v-select
                                class="common-select common-select-editmode"
                                :placeholder="$t('v4.Account')"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col :md="2">
                        <b-form-group class="b-form-group-flatpickr-custom">
                            <b-input-group
                                class="common-select common-select-editmode"
                            >
                                <flat-pickr
                                    ref="flatPickr"
                                    class="form-control create-form-flatpicker border-right-0"
                                    placeholder="YYYY-MM"
                                    :config="datePickConfig"
                                    value="2024-05"
                                />
                                <b-input-group-prepend
                                    is-text
                                >
                                    <SVGCalendarNumberIcon/>
                                </b-input-group-prepend>
                            </b-input-group>
                        </b-form-group>
                    </b-col>
                </b-row>
            </div>
            <div style="touch-action: none">
                <grid-layout
                    :layout.sync="currentLayout"
                    :colNum="colNum"
                    :rowHeight="rowHeight"
                    :verticalCompact="true"
                    :useCssTransforms="true"
                    :isDraggable="!viewTypeIsMobile"
                    :isResizable="!viewTypeIsMobile"
                    :responsive="false"
                    :margin="margin"
                    :isBounded="true"
                >
                    <grid-item v-for="(item, index) in currentLayout"
                        :id="isTourEditStep5(item.i, index) ? 'tour-edit-dashboard-step-5' : ''"
                        :ref="item.i"
                        class="card border-edit-mode"
                        :preserveAspectRatio="false"
                        :key="index"
                        :x="item.x"
                        :y="item.y"
                        :w="item.w"
                        :h="item.h"
                        :minW="item.minW || item.w"
                        :minH="item.minH || item.h"
                        :maxW="item.maxW || 24"
                        :maxH="item.maxH || 100"
                        :i="item.i"
                        :resizeOption="{ edges: { bottom: true, right: true }}"
                        @moved="movedEvent"
                        @resize="resizeEvent"
                    >
                        <a
                            v-show="!viewTypeIsMobile"
                            :id="isTourEditStep3(item.i, index) ? 'tour-edit-dashboard-step-3' : ''"
                            class="minus"
                            @click="() => removeWidgetItem(item.i)"
                        >
                            <SVGMinusIcon/>
                        </a>
                        <component
                            class="pointer-events-none"
                            :is="item.i"
                            :editMode="true"
                        >
                        </component>
                    </grid-item>
                </grid-layout>
            </div>
        </b-col>
    </b-row>
</template>

<script>
import bus from '@/libs/bus'
import cardType from '../cardType'
import { defaultLayoutMap, defaultLayout } from '../_store/dummy-data'
import { heightFade } from '@core/directives/animations'
import flatPickr from 'vue-flatpickr-component'
import MonthSelectPlugin from '@/libs/monthSelect'
import vSelect from 'vue-select'
import { GridLayout, GridItem } from 'vue-grid-layout'
import { ref, watch, computed, getCurrentInstance, onMounted } from '@vue/composition-api'
import useDashboardRender from '@/composables/useDashboardRender'
import useTour from '@/composables/useTour'
import WidgetCheckbox from './WidgetCheckbox'
import useDashboardHeader from '@/composables/useDashboardHeader'
import useWidget from '@/composables/useWidget'
import SVGMinusIcon from '@/assets/images/icons/minus.svg'
import SVGCalendarNumberIcon from '@/assets/images/icons/calendar-with-number.svg'
const CardTotalCost = () => import('./CardTotalCost.vue')
const CardDownload = () => import('./CardDownload.vue')
const CardMonths = () => import('./CardMonths.vue')
const CardWeeks = () => import('./CardWeeks.vue')
const CardDates = () => import('./CardDates.vue')
const CardProducts = () => import('./CardProducts.vue')
const CardRegions = () => import('./CardRegions.vue')
const CardStatistics = () => import('./CardStatistics.vue')
const CardBanner = () => import('./CardBanner.vue')
const CardBoard = () => import('./CardBoard.vue')
const CardDatesDetail = () => import('./CardDatesDetail.vue')
const SummaryCardSidebar = () => import('./summary/SideBar.vue')
const SummaryCardProducts = () => import('./summary/ProductCard.vue')
const SummaryCardRegions = () => import('./summary/RegionCard.vue')
const SummaryCardDates = () => import('./summary/DailyLine.vue')
const SummaryCardWeeks = () => import('./summary/WeeklyHeatmap.vue')
export default {
    components: {
        SVGMinusIcon,
        flatPickr,
        vSelect,
        GridLayout,
        GridItem,
        CardTotalCost,
        CardDownload,
        CardMonths,
        CardWeeks,
        CardDates,
        CardProducts,
        CardRegions,
        CardStatistics,
        CardBoard,
        CardBanner,
        CardDatesDetail,
        SummaryCardSidebar,
        SummaryCardProducts,
        SummaryCardRegions,
        SummaryCardDates,
        SummaryCardWeeks,
        WidgetCheckbox,
        SVGCalendarNumberIcon
    },
    directives: {
        'height-fade': heightFade
    },
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const actionIcon = {
            minus: require('@/assets/images/icons/minus.svg')
        }
        const isTourEditStep3 = (cardName, index) => {
            return cardName === cardType.CARD_TOTAL_COST
        }
        const isTourEditStep5 = (cardName, index) => {
            return cardName === cardType.CARD_DATES
        }
        /** Magic Number */
        const colNum = 24
        const layoutTop = 160
        const rowHeight = 10
        const margin = [28, 28]
        const datePickConfig = {
            wrap: true,
            allowInput: false,
            minDate: '2023-06',
            maxDate: '2024-05',
            plugins: [
                new MonthSelectPlugin({
                    shorthand: true,
                    dateFormat: 'Y-m'
                })
            ]
        }
        const {
            layout,
            editDashboard,
            viewType,
            windowWidth,
            getNextPosition,
            sortCardsByPosition,
            getCurrentLayoutMobile,
            updateEditDashboard,
            layoutReducer
        } = useDashboardRender()
        const { widgetCardModel, handleWidgetCardModel } = useWidget()
        const {
            isResetMode,
            updateResetMode
        } = useDashboardHeader()
        const initialLayout = ref([...editDashboard.value.map(item => ({ ...item }))])
        const currentLayout = ref(editDashboard.value)
        const sortedCards = ref(widgetCardModel.value)
        const resetFlag = ref(isResetMode.value)
        const viewTypeIsMobile = computed(() => viewType.value === 'mobile')
        const { isDoneTourEditDashboard, cancelTour } = useTour()
        const removeWidgetItem = (removeItem) => {
            const newCardModels = sortedCards.value.filter(card => card !== removeItem)
            handleWidgetCardModel(newCardModels)
        }

        const movedEvent = (i, newX, newY) => {
            sortedCards.value = sortCardsByPosition(currentLayout.value)
            initialLayout.value = currentLayout.value
            updateResetMode(false)
        }
        const resizeEvent = () => {
            updateResetMode(false)
        }
        watch(isResetMode, (value, oldValue) => {
            if (value) {
                resetFlag.value = false
                currentLayout.value = [...defaultLayout.map(item => ({ ...item }))]
                const values = defaultLayout.map(item => item.i)
                handleWidgetCardModel(values)
            }
        })
        watch(currentLayout, (value, oldValue) => {
            if (viewTypeIsMobile.value) return
            updateEditDashboard(value)
        })

        watch(widgetCardModel, (value, oldValue) => {
            if (!resetFlag.value && isResetMode.value) {
                sortedCards.value = sortCardsByPosition(currentLayout.value)
                resetFlag.value = true
                return
            }
            updateResetMode(false)
            // remove
            if (value.length < oldValue.length) {
                const removedCard = oldValue.find(v => !value.includes(v))
                const cardIndex = sortedCards.value.findIndex(cardName => cardName === removedCard)
                sortedCards.value.splice(cardIndex, 1)
                const layoutIndex = currentLayout.value.findIndex(item => item.i === removedCard)
                currentLayout.value.splice(layoutIndex, 1)
            // add
            } else {
                const addedCard = value.find(v => !oldValue.includes(v))
                if (!addedCard) return
                sortedCards.value.push(addedCard)
                const { w, h, minW, minH, maxW, maxH, moved } = defaultLayoutMap[addedCard]
                const { x, y } = getNextPosition(currentLayout.value, w, h)
                currentLayout.value.push({
                    x,
                    y,
                    w,
                    h,
                    maxH,
                    maxW,
                    minH,
                    minW,
                    i: addedCard,
                    moved
                })
                vm.$nextTick(() => {
                    const top = Math.round(rowHeight * y) + (y - 1) * margin[0] + layoutTop
                    setTimeout(() => {
                        window.scrollTo({ top, behavior: 'smooth' })
                    }, 200)
                })
            }
            if (!viewTypeIsMobile.value) {
                initialLayout.value = currentLayout.value
            }
        })

        if (viewTypeIsMobile.value) {
            sortedCards.value = sortCardsByPosition(currentLayout.value)
            currentLayout.value = getCurrentLayoutMobile(sortedCards.value, currentLayout.value.reduce(layoutReducer, {}))[windowWidth.value <= 576 ? 'xsLayout' : 'smLayout']
        }
        watch(windowWidth, (value, oldValue) => {
            if (viewTypeIsMobile.value) {
                cancelTour()
                sortedCards.value = sortCardsByPosition(currentLayout.value)
                currentLayout.value = getCurrentLayoutMobile(sortedCards.value, currentLayout.value.reduce(layoutReducer, {}))[value <= 576 ? 'xsLayout' : 'smLayout']
            } else {
                currentLayout.value = initialLayout.value
            }
        })
        onMounted(() => {
            if (isDoneTourEditDashboard.value || viewTypeIsMobile.value) return
            bus.$emit('tour-start:dashboardEdit')
        })
        return {
            isTourEditStep3,
            isTourEditStep5,
            colNum,
            rowHeight,
            margin,
            datePickConfig,
            viewTypeIsMobile,
            layout,
            initialLayout,
            currentLayout,
            sortedCards,
            actionIcon,
            movedEvent,
            resizeEvent,
            removeWidgetItem
        }
    }
}
</script>
<style>
.common-select-editmode .vs__dropdown-toggle {
    background: #F6F5F8;
}
.common-select-editmode .form-control,
.common-select-editmode .input-group-text {
    background-color: #F6F5F8 !important;
}
.border-edit-mode {
    border: 1px solid #D9D9D9 !important;
    border-radius: 8px;
    /* border-radius: 15px; */
}
.minus {
    cursor: pointer;
    position: absolute;
    top: 9px;
    right: 11px;
    z-index: 2;
    width: 24px;
    height: 24px;
    border-radius: 50%;
}
.dark-layout .common-select-editmode .input-group-text {
    background-color: #283046 !important;
}
</style>
